<template>
  <VSnackbar
    v-model="showNotification"
    :timeout="-1"
    color="info"
    location="top right"
    multi-line
  >
    <div class="d-flex align-center">
      <VIcon class="mr-3 mdi" size="28">{{ requestIcon }}</VIcon>
      <div>
        <div class="text-subtitle-1 font-weight-bold">{{ requestTitle }}</div>
        <div class="text-body-2">
          {{ currentRequest?.tableName || 'Table' }} - Order #{{ currentRequest?.orderNumber }}
        </div>
        <div v-if="currentRequest?.notes" class="text-caption mt-1">
          "{{ currentRequest.notes }}"
        </div>
      </div>
      <VChip v-if="requestQueue.length > 1" class="ml-3" color="white" size="x-small" variant="outlined">
        +{{ requestQueue.length - 1 }}
      </VChip>
    </div>

    <template #actions>
      <VBtn color="white" size="small" variant="text" @click="acknowledgeRequest">
        <VIcon class="mr-1 mdi" size="small">mdi-check</VIcon>
        Done
      </VBtn>
      <VBtn color="white" size="small" variant="text" @click="dismissRequest">
        {{ requestQueue.length > 1 ? 'Next' : 'Dismiss' }}
      </VBtn>
    </template>
  </VSnackbar>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { usePosEventBus } from '~/composables/usePosEventBus'

const { onTableRequestNew, onTableRequestAcknowledged } = usePosEventBus()

const showNotification = ref(false)
const requestQueue = ref([])
const acknowledging = ref(false)

const currentRequest = computed(() => requestQueue.value[0] || null)

const requestTypeLabels = {
  water: 'Water Requested',
  bill: 'Bill Requested',
  napkins: 'Napkins Requested',
  cutlery: 'Cutlery Requested',
  server: 'Server Needed',
  other: 'Assistance Requested',
}

const requestTypeIcons = {
  water: 'mdi-water',
  bill: 'mdi-receipt-text',
  napkins: 'mdi-paper-roll',
  cutlery: 'mdi-silverware-fork-knife',
  server: 'mdi-account-tie',
  other: 'mdi-hand-wave',
}

const requestTitle = computed(() => {
  if (!currentRequest.value) return 'Table Request'
  return requestTypeLabels[currentRequest.value.requestType] || 'Assistance Requested'
})

const requestIcon = computed(() => {
  if (!currentRequest.value) return 'mdi-hand-wave'
  return requestTypeIcons[currentRequest.value.requestType] || 'mdi-hand-wave'
})

const config = useRuntimeConfig()
const userStore = useUserStore()

const acknowledgeRequest = async () => {
  if (!currentRequest.value || acknowledging.value) return

  acknowledging.value = true
  try {
    await $fetch(`${config.public.apiUrl}/api/table-requests/${currentRequest.value.requestId}/acknowledge`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${userStore.token}`,
      },
    })
  } catch (error) {
    console.error('Failed to acknowledge request:', error)
  } finally {
    acknowledging.value = false
    dismissRequest()
  }
}

const dismissRequest = () => {
  requestQueue.value.shift()
  if (requestQueue.value.length === 0) {
    showNotification.value = false
  }
}

const handleNewRequest = (payload) => {
  console.log('[TableRequestNotification] New request:', payload)
  requestQueue.value.push(payload)
  showNotification.value = true

  if (typeof window !== 'undefined' && 'vibrate' in navigator) {
    navigator.vibrate([200, 100, 200])
  }
}

const handleAcknowledged = (payload) => {
  console.log('[TableRequestNotification] Request acknowledged:', payload)
  const index = requestQueue.value.findIndex(r => r.requestId === payload.requestId)
  if (index !== -1) {
    requestQueue.value.splice(index, 1)
    if (requestQueue.value.length === 0) {
      showNotification.value = false
    }
  }
}

let unsubscribeNew = null
let unsubscribeAck = null

onMounted(() => {
  unsubscribeNew = onTableRequestNew(handleNewRequest)
  unsubscribeAck = onTableRequestAcknowledged(handleAcknowledged)
})

onUnmounted(() => {
  if (unsubscribeNew) unsubscribeNew()
  if (unsubscribeAck) unsubscribeAck()
})
</script>
